import { App, URLOpenListenerEvent } from "@capacitor/app";
import { createRouter, createWebHistory } from "@ionic/vue-router";
import { FabriqStore, useFabriqStore } from "@/store/fabriq";
import { AuthClient } from "@/services/auth";
import { Browser } from "@capacitor/browser";
import config from "@/config";
import { localLogger } from "@/utils/localLogger";
import { useClientStore } from "@/store/client";
import { RouteRecordRaw } from "vue-router";
import ApplicationConfigurationRouterOutlet from "@/views/ApplicationConfiguration/RouterOutlet.vue";
import ApplicationConfigurationIndexPage from "@/views/ApplicationConfiguration/IndexPage.vue";
import ApplicationConfigurationQRCodePage from "@/views/ApplicationConfiguration/QRCodePage.vue";
import ApplicationConfigurationSubdomainPage from "@/views/ApplicationConfiguration/SubdomainPage.vue";
import MainPage from "@/views/MainPageTabs.vue";
import LoginPage from "@/views/LoginPage.vue";
import TicketsPage from "@/views/TicketsPage.vue";
import TicketPage from "@/views/TicketPage.vue";
import EventsPage from "@/views/EventsPage.vue";
import InstancePage from "@/views/InstancePage.vue";
import AccountPage from "@/views/AccountPage.vue";
import SearchPage from "@/views/SearchPage.vue";
import RoutinesPage from "@/views/RoutinesPage.vue";
import IndicatorsPage from "@/views/IndicatorsPage.vue";
import KnowledgePage from "@/views/KnowledgePage.vue";
import NotificationsPage from "@/views/NotificationsPage.vue";
import EventPage from "@/views/EventPage.vue";
import DocumentPage from "@/views/DocumentPage.vue";
import DocumentInfoPage from "@/views/DocumentInfoPage.vue";
import { AuthenticationFailedException } from "@/classes/errors";

// Paths that concern an entity (and not a list) are suffixed by `D`, for details.
const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: ApplicationConfigurationRouterOutlet,
    beforeEnter: (_from, _to, next) => {
      // eslint-disable-next-line fabriq-webapp/vue-composable-rules
      const fabriqStore = useFabriqStore();
      if (fabriqStore.url) {
        if (fabriqStore.connected) return next({ name: "Tickets" });
        return next({ name: "Login" });
      }
      return next();
    },
    children: [
      {
        name: "ApplicationConfigurationIndex",
        path: "",
        component: ApplicationConfigurationIndexPage,
      },
      {
        name: "ApplicationConfigurationQRCode",
        path: "/qrcode",
        component: ApplicationConfigurationQRCodePage,
      },
      {
        name: "ApplicationConfigurationSubdomain",
        path: "/subdomain",
        component: ApplicationConfigurationSubdomainPage,
      },
    ],
  },
  {
    name: "Login",
    path: "/login/:code?",
    component: LoginPage,
  },
  {
    name: "TicketD",
    path: "/tickets/:id/:instanceId?",
    component: TicketPage,
  },
  {
    name: "DocumentD",
    path: "/documents/:id",
    component: DocumentPage,
    beforeEnter: () => {
      // eslint-disable-next-line fabriq-webapp/vue-composable-rules
      const clientStore = useClientStore();
      return clientStore.config.hasKnowledge;
    },
  },
  {
    name: "DocumentInfoD",
    path: "/documents/:id/info",
    component: DocumentInfoPage,
    beforeEnter: () => {
      // eslint-disable-next-line fabriq-webapp/vue-composable-rules
      const clientStore = useClientStore();
      return clientStore.config.hasKnowledge;
    },
  },

  {
    name: "InstanceD",
    path: "/instances/:instanceId",
    component: InstancePage,
  },
  {
    name: "Account",
    path: "/account",
    component: AccountPage,
  },
  {
    name: "Search",
    path: "/search",
    component: SearchPage,
  },
  {
    name: "Notifications",
    path: "/notifications",
    component: NotificationsPage,
  },
  {
    path: "/main",
    component: MainPage,
    redirect: "/main/tickets",
    children: [
      {
        name: "Tickets",
        path: "tickets",
        component: TicketsPage,
      },
      {
        name: "Events",
        path: "events",
        component: EventsPage,
      },
      {
        name: "Indicators",
        path: "indicators",
        component: IndicatorsPage,
        beforeEnter() {
          // eslint-disable-next-line fabriq-webapp/vue-composable-rules
          const clientStore = useClientStore();
          return clientStore.hasKpi;
        },
      },
      {
        name: "Knowledge",
        path: "knowledge",
        component: KnowledgePage,
        beforeEnter() {
          // eslint-disable-next-line fabriq-webapp/vue-composable-rules
          const clientStore = useClientStore();
          return clientStore.config.hasKnowledge;
        },
      },
      {
        name: "Routines",
        path: "routines",
        component: RoutinesPage,
      },
    ],
  },
  {
    name: "EventD",
    path: "/events/:id",
    component: EventPage,
  },
  {
    name: "EventCreationD",
    path: "/events/create/:eventTypeId",
    component: EventPage,
  },
];
const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const fabriqStore = useFabriqStore();
  if (fabriqStore.initializationState.type === "pending") {
    await fabriqStore.initializeApiClients();
  }
  if (to.path === "/config") {
    if (!fabriqStore.url) {
      await configureApplicationFromQRCode(window.location.href);
    }
    return next({ name: "Login" });
  }
  const isApplicationConfigurationPage = String(to.name).startsWith(
    "ApplicationConfiguration"
  );
  if (!fabriqStore.url && !isApplicationConfigurationPage) return next("/");
  if (!fabriqStore.url && isApplicationConfigurationPage) return next();
  if (to.name) {
    fabriqStore.setPage(String(to.name).toLocaleLowerCase());
  }
  if (to.path === "/saml-auth-code") {
    const url =
      window.location.href.replace(to.fullPath.substring(1), "") +
      to.fullPath.substring(1);
    await finalizeAuthentication(fabriqStore, url);
    return next("/login/initialize");
  }
  if (to.fullPath.includes("/documents?document")) {
    fabriqStore.setPageToOpenAfterLogin(
      `/documents/${to.fullPath.split("?document=")[1]}`
    );
  }
  if (to.name !== "Login" && !fabriqStore.connected)
    return next({ name: "Login" });
  if (
    to.name === "Login" &&
    fabriqStore.connected &&
    fabriqStore.initializationState.type !== "pending"
  )
    return next({ name: "Tickets" });
  if (
    to.name === "InstanceD" &&
    from.name !== "Routines" &&
    from.name !== "TicketD"
  ) {
    fabriqStore.$patch((state) => (state.hideTabs = false));
    return next({ name: from.name ?? "Tickets" });
  }
  next();
});

export const finalizeAuthentication = async (
  fabriqStore: FabriqStore,
  url: string
) => {
  await localLogger.log(`finalizeAuthentication ${url}`);
  const eventUrl = new URL(url);
  const code = eventUrl.searchParams.get("code");
  localStorage.removeItem("ssoLogin");
  if (code) {
    try {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.hash
      );
    } catch (e) {
      await localLogger.log(
        `An error occured during browser history replace with message: ${e}`
      );
    }
    try {
      await AuthClient.finalizeFutureAuth(code);
      await localLogger.log(`finalizeAuthentication OK`);
    } catch (error) {
      await localLogger.log(
        `finalizeAuthentication AuthClient.finalizeFutureAuth failed with ${error}`
      );
      const errorMessage =
        error instanceof Error ? error.message : "finalizeFutureAuth failed";
      fabriqStore.setInitializationState({
        type: "error",
        error: new AuthenticationFailedException(
          "failedToFinalize",
          errorMessage
        ),
      });
    }
  } else {
    await localLogger.log(`finalizeAuthentication error no code`);
  }
};

export const configureApplicationFromQRCode = async (url: string) => {
  await localLogger.log(`configureApplicationFromQRCode url: ${url}`);
  const qrCodeUrlObject = new URL(url);
  const originUrl = qrCodeUrlObject.origin;
  if (originUrl) {
    await localLogger.log(
      `configureApplicationFromQRCode originUrl: ${originUrl}`
    );
    try {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.hash
      );
    } catch (e) {
      await localLogger.log(
        `An error occured during configureApplicationFromQRCode with message: ${e}`
      );
    }
    // eslint-disable-next-line fabriq-webapp/vue-composable-rules
    const fabriqStore = useFabriqStore();
    await fabriqStore.initializeApiClients(originUrl);
    await localLogger.log(`configureApplicationFromQRCode OK`);
  } else {
    await localLogger.log(`configureApplicationFromQRCode KO`);
  }
};

if (config.env !== "production") {
  window.setSAMLResponse = async (path: string) => {
    // eslint-disable-next-line fabriq-webapp/vue-composable-rules
    const fabriqStore = useFabriqStore();
    await finalizeAuthentication(fabriqStore, path);
    router.replace("/login/initialize");
  };
}

const appUrlOpener = async (event: URLOpenListenerEvent) => {
  await localLogger.log(`appUrlOpener ${event.url}`);
  Browser.close().catch(async (err) => {
    await localLogger.log(`appUrlOpener Browser.close error ${err}`);
    return Promise.resolve();
  });
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const fabriqStore = useFabriqStore();
  try {
    if (event.url.includes("/saml-auth-code")) {
      await finalizeAuthentication(fabriqStore, event.url);
    } else if (event.url.includes("/code")) {
      await configureApplicationFromQRCode(event.url);
    } else {
      await localLogger.log(
        `appUrlOpener error not saml-auth-code ${event.url}`
      );
    }
    await localLogger.log(`appUrlOpener redirect to /login/initialize`);
    router.replace("/login/initialize");
  } catch (e) {
    // eslint-disable-next-line fabriq-webapp/vue-composable-rules
    const fabriqStore = useFabriqStore();
    await localLogger.log(`appUrlOpener catch ${e}`);
    await AuthClient.logout(null);
    console.error("e", e);
    if (e instanceof Error) fabriqStore.reportSentry(e);
  }
};

App.addListener("appUrlOpen", appUrlOpener);

export default router;
